import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useBoolean } from 'react-use'
import i18n from 'i18next'

import { SetLanguage } from '@makyoapp/makyo-sdk'
import { ReqLogin, ReqLoginWith2FA, ReqLoginWithCode } from '@makyoapp/makyo-sdk/api/auth'
import { UsersAPI } from '@makyoapp/makyo-sdk/api/Users'
import { IUser, NotificationACL } from '@makyoapp/makyo-sdk/models/User'
import { fromSdkUser, UserAdapter } from '@makyoapp/makyo-common'

import { formatError } from '@/lib/error'
import { STORAGE_KEYS } from '@/shared/constants'

// const tracker = new Tracker({
//   projectKey: appConfig.openReplay.projectKey,
// })

/**
 * auth error message
 */
interface AuthError {
  status: number
  message: string
}

/**
 * authentication state
 */
interface AuthState {
  /**
   * for handling loading
   */
  isLoginPending: boolean
  /**
   * user data if logged in
   */
  userData: UserAdapter | null
}

export type UpdateUserProps = Partial<Pick<UserAdapter, 'name' | 'lang' | 'theme' | 'timezone' | 'locale'>>

interface SecondStepResult {
  result?: UserAdapter | true
  done?: () => void
}

interface ThirdStepResult {
  result?: UserAdapter
  done?: () => void
}

/**
 * Auth context value
 * this is the type to determine the required context value properties
 */
interface AuthContextValue {
  //state: AuthState
  /**
   * for handling loading
   */
  isLoginPending: boolean
  /**
   * user data if logged in
   */
  userData: UserAdapter | null

  /**
   * current user avatar
   */
  avatar?: string

  /**
   * set current user avatar
   * @param nextAvatar
   */
  setAvatar: (nextAvatar?: string) => void

  /**
   * update user avatar
   */
  updateAvatar: (src?: string) => void
  /**
   * update user name
   */
  updateProfile: (props: UpdateUserProps) => void
  /**
   * update notification settings
   */
  updateNotifications: (values: NotificationACL) => void
  /**
   * update referral code
   */
  updateReferral: (code: string) => void
  /**
   * set language
   */
  setLang: (lang: string) => void
  /**
   * login first step callback
   */
  firstStep: (email: string) => Promise<void>
  /**
   * login second step callback
   */
  secondStep: (code: string) => Promise<SecondStepResult>
  /**
   * third step login with 2FA
   */
  thirdStep: (code: string) => Promise<ThirdStepResult>
  /**
   * logout function
   * called when user logout
   */
  logout: () => void
}

/**
 * default initial auth state
 */
const initialState: AuthState = {
  isLoginPending: false,
  userData: null,
}

/**
 *
 */
export const authContextDefaults: AuthContextValue = {
  isLoginPending: false,
  userData: null,
  avatar: undefined,
  setAvatar: () => null,
  updateAvatar: () => null,
  updateProfile: () => null,
  updateNotifications: () => null,
  updateReferral: () => null,
  setLang: () => null,
  firstStep: async () => {
    // noop
  },
  secondStep: async () => {
    return {
      result: undefined,
      done: undefined,
    }
  },
  thirdStep: async () => {
    return {
      result: undefined,
      done: undefined,
    }
  },
  logout: () => null,
}

export const AuthContext = React.createContext<AuthContextValue>(authContextDefaults)

interface AuthContextProviderProp {
  children?: React.ReactNode
}

/**
 * Authentication provider
 */
export const AuthProvider = (props: AuthContextProviderProp) => {
  const session = localStorage.getItem(STORAGE_KEYS.USER_SESSION)
  let sdkUser: IUser | UserAdapter | null = null

  /**
   * getting cached info
   */
  if (session !== null) {
    try {
      sdkUser = JSON.parse(session)
      if (sdkUser) {
        // eslint-disable-next-line no-prototype-builtins
        initialState.userData = sdkUser.hasOwnProperty('_Name')
          ? fromSdkUser(sdkUser as IUser)
          : (sdkUser as UserAdapter)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const [profileUpdated, setProfileUpdated] = useBoolean(false)
  const [userState, setUserState] = useState<UserAdapter | null>(initialState.userData)
  const [avatar, setAvatar] = useState<string | undefined>(initialState.userData?.avatar)
  const [isLoginPending, setLoginPending] = useBoolean(false)

  // useEffect(() => {
  //   if (import.meta.env.MODE === 'production') {
  //     tracker.start({
  //       metadata: {
  //         environment: import.meta.env.MODE,
  //       },
  //     })
  //   }
  // }, [])

  const setUserData = useCallback(
    (user: UserAdapter | null) => {
      setUserState(user)
      setAvatar(user?.avatar)

      if (!user) {
        localStorage.removeItem(STORAGE_KEYS.USER_SESSION)
      } else {
        // remove email from cached value in localStorage
        const { email: _, ...tobeCached } = user
        localStorage.setItem(STORAGE_KEYS.USER_SESSION, JSON.stringify(tobeCached))

        // if (import.meta.env.MODE === 'production') {
        //   tracker.setUserID(user.id)
        //   tracker.setMetadata('email', user.email || '')
        //   tracker.setMetadata('name', user.name || '')
        //   tracker.setMetadata('lang', user.lang || '')
        //   tracker.setMetadata('timezone', user.timezone || '')
        //   tracker.setMetadata('locale', user.locale || '')
        //   tracker.setMetadata('active', user.active ? 'true' : 'false')
        //   tracker.setMetadata('isDeleted', user.isDeleted ? 'true' : 'false')
        // }
      }
    },
    [setUserState]
  )

  const firstStep = async (email: string) => {
    setLoginPending(true)
    try {
      await ReqLogin(email)
      setLoginPending(false)
    } catch (e: unknown) {
      setLoginPending(false)
      if (typeof e === 'object') {
        throw new Error((e as AuthError).message)
      } else {
        throw e
      }
    }
  }

  const setLang = useCallback((lang: string) => {
    i18n.changeLanguage(lang).then(() => {
      SetLanguage(lang)
    })
  }, [])

  const secondStep = async (code: string): Promise<SecondStepResult> => {
    setLoginPending(true)

    try {
      const result = await ReqLoginWithCode(code)
      if (!result.TwoFARequired && result.User) {
        const toUser = fromSdkUser(result.User)
        setUserData(toUser)
        setLang(toUser.lang)
        return {
          result: toUser,
          done: () => setLoginPending(false),
        }
      } else {
        setLoginPending(false)
        return {
          result: result.TwoFARequired as true,
          done: undefined,
        }
      }
    } catch (e: unknown) {
      setLoginPending(false)
      if (typeof e === 'object') {
        throw new Error((e as AuthError).message)
      } else {
        throw e
      }
    }
  }

  const thirdStep = async (code: string): Promise<ThirdStepResult> => {
    setLoginPending(true)
    try {
      const result = await ReqLoginWith2FA(code)
      const toUser = fromSdkUser(result.User)
      setUserData(toUser)
      setLang(toUser.lang)
      return {
        result: toUser,
        done: () => setLoginPending(false),
      }
    } catch (e: unknown) {
      setLoginPending(false)
      if (typeof e === 'object') {
        throw new Error((e as AuthError).message)
      } else {
        throw e
      }
    }
  }

  const logout = useCallback(() => {
    setProfileUpdated(false)
    setLoginPending(false)
    setUserData(null)
    localStorage.removeItem(STORAGE_KEYS.USER_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.PROJECT_FILTER)
    localStorage.removeItem('collapsedState')
    localStorage.removeItem('sidebarWidth')
    i18n.changeLanguage('en').catch(console.error)
  }, [setUserData, setLoginPending, setProfileUpdated])

  const updateAvatar = (avatar?: string) => {
    if (userState) {
      const currentAvatar = userState.avatar
      if (currentAvatar) {
        const isBlob = currentAvatar.startsWith('blob:')
        if (isBlob) {
          URL.revokeObjectURL(currentAvatar)
        }
      }

      const newData = { ...userState, avatar }
      setUserData(newData)
    }
  }

  const updateProfile = ({ name, lang, timezone, theme, locale }: UpdateUserProps) => {
    if (userState) {
      const newData = {
        ...userState,
        name: name || userState.name,
        lang: lang || userState.lang,
        timezone: timezone || userState.timezone,
        theme: theme || userState.theme,
        locale: locale || userState.locale,
      }
      setUserData(newData)
    }
  }

  const updateNotifications = (notifications: NotificationACL) => {
    if (userState) {
      const newData = { ...userState, notifications }
      setUserData(newData)
    }
  }

  const updateReferral = (referral: string) => {
    if (userState) {
      const newData = { ...userState, referral }
      setUserData(newData)
    }
  }

  /**
   * update data from server then update cache
   * only when profile not updated yet
   */
  useEffect(() => {
    if (userState !== null && !profileUpdated) {
      UsersAPI.ReqGet()
        .then((res) => {
          // TODO: sdk expose deletion
          //const deletion = res._Deletion
          //const isDeleted = Boolean(res._Deletion._IsDeleted)
          const newData = fromSdkUser(res)
          setUserData(newData)
          setLang(newData.lang)
          setProfileUpdated(true)
        })
        .catch((err) => {
          const { message, status } = formatError(err)

          if (status === 401) {
            logout()
          } else if (message?.toLowerCase()?.includes('unauthorized')) {
            return
          } else {
            toast.dismiss('for-all')
            toast.error(message, {
              containerId: 'for-all',
            })
          }
        })
    }
  }, [setProfileUpdated, userState, setUserData, logout, profileUpdated, setLang])

  return (
    <AuthContext.Provider
      value={{
        userData: userState,
        isLoginPending,
        avatar,
        setAvatar,
        updateAvatar,
        updateProfile,
        updateNotifications,
        updateReferral,
        setLang,
        firstStep,
        secondStep,
        thirdStep,
        logout,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  )
}
